import { Account, AccountBankAccount } from "models/Account";
import { Invoice } from "models/Invoice";
import { Project } from "models/Project";

export interface ProjectPayoutRow {
    projectId: string;
    projectName: string;
    tenantId: string;
    currency: string;
    workerId: string;
    workerName: string;
    bankAccount?: AccountBankAccount;
    workerInvoice?: Invoice;
    platformFeeInvoice?: Invoice;
    gross: number;
    platformFee: number;
    net: number;
    pendingHours: number;
    hasDraftInvoice: boolean;
    missingPlatformFeeInvoice: boolean;
    alreadyPaid: boolean;
}

export interface SepaPaymentFile {
    filename: string;
    xml: string;
    paymentCount: number;
    controlSum: number;
}

const roundCurrency = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

export const dateKey = (value: Date): string => {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export const shiftDays = (value: Date, days: number): Date => {
    const shifted = new Date(value);
    shifted.setDate(shifted.getDate() + days);
    return shifted;
};

export const mondayFor = (value: Date): Date =>
    shiftDays(new Date(value.getFullYear(), value.getMonth(), value.getDate()), -((value.getDay() + 6) % 7));

export const isoWeekFor = (value: Date): { year: number; week: number } => {
    const date = new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));
    const day = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - day);
    const year = date.getUTCFullYear();
    const yearStart = new Date(Date.UTC(year, 0, 1));
    return { year, week: Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7) };
};

export const invoiceNameMatchesWeek = (invoice: Invoice, weekStart: Date): boolean => {
    const namedWeek = invoice.name.match(/\bweek\s+(\d{1,2})\b/i);
    return namedWeek !== null && Number(namedWeek[1]) === isoWeekFor(weekStart).week;
};

const primaryBankAccount = (account?: Account): AccountBankAccount | undefined => {
    const usable = (account?.bank_accounts ?? []).filter(candidate => !!normalizeIban(candidate.iban));
    return usable.find(candidate => candidate.primary) ?? usable[0];
};

export const buildProjectPayoutRows = (
    project: Project,
    accounts: Account[],
    invoices: Invoice[],
    weekStart: Date,
): ProjectPayoutRow[] => {
    const weekKey = dateKey(weekStart);
    const weekEndKey = dateKey(shiftDays(weekStart, 7));
    const accountsById = new Map(accounts.map(account => [account.id, account]));
    const weekInvoices = invoices.filter(invoice =>
        invoice.project_id === project.id && !invoice.quote &&
        // Monday's invoices belong to the preceding payment week.
        dateKey(mondayFor(shiftDays(invoice.GetCreatedDate(), -1))) === weekKey
    );

    return project.workers.map(worker => {
        const account = accountsById.get(worker.account_id);
        const workerInvoice = weekInvoices.find(invoice => invoice.supplier === worker.account_id);
        const platformFeeInvoice = weekInvoices.find(invoice =>
            !invoice.supplier && invoice.customer === worker.account_id
        );
        const gross = roundCurrency(workerInvoice?.CalcGrandTotal() ?? 0);
        const platformFee = roundCurrency(platformFeeInvoice?.CalcGrandTotal() ?? 0);
        const entries = (worker.entries ?? []).filter(entry => {
            const entryDate = entry.date.slice(0, 10);
            return entryDate >= weekKey && entryDate < weekEndKey;
        });

        return {
            projectId: project.id,
            projectName: project.name,
            tenantId: project.tenant,
            currency: project.currency,
            workerId: worker.account_id,
            workerName: account?.legal_name ?? worker.account_id,
            bankAccount: primaryBankAccount(account),
            workerInvoice,
            platformFeeInvoice,
            gross,
            platformFee,
            net: roundCurrency(gross - platformFee),
            pendingHours: entries.filter(entry => entry.status === "pending_approval").length,
            hasDraftInvoice: !!workerInvoice?.draft || !!platformFeeInvoice?.draft,
            missingPlatformFeeInvoice: !!workerInvoice && !platformFeeInvoice,
            alreadyPaid: !!workerInvoice?.IsPaid(),
        };
    }).sort((first, second) => first.workerName.localeCompare(second.workerName, "nl"));
};

export const buildWeeklyProjectPayoutRows = (
    projects: Project[],
    accounts: Account[],
    invoices: Invoice[],
    weekStart: Date,
): ProjectPayoutRow[] => projects
    .flatMap(project => buildProjectPayoutRows(project, accounts, invoices, weekStart))
    .sort((first, second) =>
        first.projectName.localeCompare(second.projectName, "nl") ||
        first.workerName.localeCompare(second.workerName, "nl")
    );

export const normalizeIban = (value?: string): string =>
    (value ?? "").replace(/\s+/g, "").toUpperCase();

export const isValidIban = (value?: string): boolean => {
    const iban = normalizeIban(value);
    if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
    const rearranged = iban.slice(4) + iban.slice(0, 4);
    let remainder = 0;
    for (const character of rearranged) {
        const numeric = /[A-Z]/.test(character) ? String(character.charCodeAt(0) - 55) : character;
        for (const digit of numeric) remainder = (remainder * 10 + Number(digit)) % 97;
    }
    return remainder === 1;
};

const xmlEscape = (value: string): string => value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const sepaText = (value: string, maximumLength: number): string =>
    xmlEscape(value.replace(/[^A-Za-z0-9 '\-?:().,+/]/g, " ").replace(/\s+/g, " ").trim().slice(0, maximumLength));

export const buildSepaPaymentFile = ({
    weekStart,
    tenantName,
    tenantIban,
    rows,
    generatedAt = new Date(),
}: {
    weekStart: Date;
    tenantName: string;
    tenantIban: string;
    rows: ProjectPayoutRow[];
    generatedAt?: Date;
}): SepaPaymentFile => {
    if (!isValidIban(tenantIban)) throw new Error("A valid tenant IBAN is required");
    if (rows.some(row => row.pendingHours > 0)) throw new Error("All hours must be approved before creating the payment file");
    const payments = rows.filter(row => !!row.workerInvoice && !row.alreadyPaid);
    if (new Set(payments.map(row => row.tenantId)).size > 1) {
        throw new Error("A payment file can only contain projects from one tenant");
    }
    if (payments.some(row => row.currency.toUpperCase() !== "EUR")) {
        throw new Error("SEPA payment files only support EUR payouts");
    }
    if (payments.some(row =>
        row.net <= 0 || row.hasDraftInvoice || row.missingPlatformFeeInvoice || !isValidIban(row.bankAccount?.iban)
    )) throw new Error("All payable invoices must be complete, final and have a valid IBAN");
    if (!payments.length) throw new Error("There are no ready payments for this week");

    const controlSum = roundCurrency(payments.reduce((total, payment) => total + payment.net, 0));
    const timestamp = generatedAt.toISOString().replace(/\.\d{3}Z$/, "Z");
    const week = isoWeekFor(weekStart);
    const generatedKey = timestamp.replace(/[-:]/g, "");
    const messageId = `PAY-${week.year}W${String(week.week).padStart(2, "0")}-${generatedKey}`.slice(0, 35);
    const executionDate = dateKey(shiftDays(weekStart, 8));
    const transactions = payments.map(row => {
        const invoice = row.workerInvoice!;
        const accountName = row.bankAccount?.account_name?.trim() || row.workerName;
        return `      <CdtTrfTxInf>
        <PmtId><EndToEndId>${sepaText(invoice.id, 35)}</EndToEndId></PmtId>
        <Amt><InstdAmt Ccy="EUR">${row.net.toFixed(2)}</InstdAmt></Amt>
        <Cdtr><Nm>${sepaText(accountName, 70)}</Nm></Cdtr>
        <CdtrAcct><Id><IBAN>${normalizeIban(row.bankAccount?.iban)}</IBAN></Id></CdtrAcct>
        <RmtInf><Ustrd>${sepaText(invoice.GetDisplayName() || invoice.id, 140)}</Ustrd></RmtInf>
      </CdtTrfTxInf>`;
    }).join("\n");

    const xml = `<?xml version="1.0" encoding="UTF-8"?>
<Document xmlns="urn:iso:std:iso:20022:tech:xsd:pain.001.001.09">
  <CstmrCdtTrfInitn>
    <GrpHdr>
      <MsgId>${sepaText(messageId, 35)}</MsgId>
      <CreDtTm>${timestamp}</CreDtTm>
      <NbOfTxs>${payments.length}</NbOfTxs>
      <CtrlSum>${controlSum.toFixed(2)}</CtrlSum>
      <InitgPty><Nm>${sepaText(tenantName, 70)}</Nm></InitgPty>
    </GrpHdr>
    <PmtInf>
      <PmtInfId>${sepaText(`${messageId}-1`, 35)}</PmtInfId>
      <PmtMtd>TRF</PmtMtd>
      <BtchBookg>true</BtchBookg>
      <NbOfTxs>${payments.length}</NbOfTxs>
      <CtrlSum>${controlSum.toFixed(2)}</CtrlSum>
      <PmtTpInf><SvcLvl><Cd>SEPA</Cd></SvcLvl></PmtTpInf>
      <ReqdExctnDt><Dt>${executionDate}</Dt></ReqdExctnDt>
      <Dbtr><Nm>${sepaText(tenantName, 70)}</Nm></Dbtr>
      <DbtrAcct><Id><IBAN>${normalizeIban(tenantIban)}</IBAN></Id></DbtrAcct>
      <DbtrAgt><FinInstnId><Othr><Id>NOTPROVIDED</Id></Othr></FinInstnId></DbtrAgt>
      <ChrgBr>SLEV</ChrgBr>
${transactions}
    </PmtInf>
  </CstmrCdtTrfInitn>
</Document>`;

    return {
        filename: `betalingen-week-${String(week.week).padStart(2, "0")}-${week.year}.xml`,
        xml,
        paymentCount: payments.length,
        controlSum,
    };
};
